/**
 * `Names: AssetNamesApi` (W1-07b formats-package; contract formats/types.ts; formats-and-packaging.md P§2.1,
 * DESIGN.md §7.3): package paths that Phira, RPE and every unzip tool handle, and download file names.
 *
 * `sanitize(desired, taken, role)`:
 *  - '/'-separated (backslashes become separators), no empty, '.' or '..' segments, no leading '/';
 *  - ASCII only: accents and compatibility forms are folded (`é` -> `e`, full-width `Ａ` -> `A`), other non-ASCII
 *    characters are dropped and a short checksum of the original segment is appended so different names stay
 *    different (`曲绘.png` -> `file-1a2b3c4d.png`); characters Windows refuses (`: * ? " < > |`, and separators that
 *    only appear through folding) become `_`, control characters are removed; Windows device names (CON, NUL, COM1,
 *    ..., also with an extension) get a leading `_`; trailing dots and spaces are removed (Windows drops them);
 *    segments longer than 120 characters are shortened with a checksum;
 *  - idempotent: a sanitised path comes back unchanged (an imported asset keeps its name on export);
 *  - never a reserved name: the manifests and generated files at the root (info.yml, info.txt, info.csv,
 *    extra.json, phiedit.json), `export.json` (Phira would treat the archive as a batch export), `line.png` (the
 *    built-in line texture name), anything ending in `click.png` (Phira's import would treat the archive as a
 *    resource pack); for role 'hitsound' also exactly tap.mp3 / flick.mp3 / drag.mp3, which Phira maps to its
 *    built-in sounds (renamed into `sfx/`);
 *  - unique within `taken`, compared case-insensitively (Windows and macOS unpack case-insensitively): `-2`, `-3`,
 *    ... before the extension.
 * `packageFileName` builds "Song Title [IN Lv.14].pez" for downloads: characters file systems refuse become `_`,
 * control characters are removed, whitespace collapsed; non-ASCII stays (it is a file on the user's disk). Without
 * a title it uses the optional `fallback` (the UI passes its localised "Untitled"; `exportPackage` the project's RPE
 * id), else the language-neutral `NEUTRAL_FILE_NAME`.
 */

import type { AssetRole } from '../../core/model/types';
import type { AssetNamesApi } from '../types';
import { crc32 } from './hash';

/** Root names the exporter writes itself or that change how Phira treats the archive. */
export const RESERVED_ROOT_NAMES: ReadonlySet<string> = new Set([
  'info.yml',
  'info.txt',
  'info.csv',
  'extra.json',
  'phiedit.json',
  'export.json',
  'line.png',
]);

/** Note hitsound strings Phira maps to its built-in sounds (never package files). */
export const BUILTIN_HITSOUND_NAMES: ReadonlySet<string> = new Set(['tap.mp3', 'flick.mp3', 'drag.mp3']);

const WINDOWS_DEVICE_RE = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
/**
 * ASCII characters Windows refuses in file names (also refused here). The separators are listed too: a segment is
 * already split, so a '/' or '\' here can only come from compatibility folding (U+FF0F, U+FF3C, ...).
 */
const WINDOWS_FORBIDDEN: ReadonlySet<string> = new Set([':', '*', '?', '"', '<', '>', '|', '/', '\\']);
const MAX_SEGMENT = 120;
/** Longer "extensions" are part of the name (a real extension is short). */
const MAX_EXT = 16;
/** Trailing dots and spaces, which Windows drops from a name. */
const TRAILING_DOTS_SPACES_RE = /[. ]+$/;
/**
 * Stands in for a character `fold` drops, so the extension is split where it was in the original name. Control
 * characters of the input are removed before, so the marker never comes from the input.
 */
const DROPPED = '\u0000';

function splitExt(name: string): { stem: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { stem: name, ext: '' };
  return { stem: name.slice(0, dot), ext: name.slice(dot) };
}

function checksum(s: string): string {
  return crc32(new TextEncoder().encode(s)).toString(16).padStart(8, '0');
}

function isCombiningMark(cp: number): boolean {
  return (cp >= 0x300 && cp <= 0x36f) || (cp >= 0x1ab0 && cp <= 0x1aff) || (cp >= 0x1dc0 && cp <= 0x1dff);
}

/**
 * ASCII text of a name: compatibility folding (`é` -> `e`, full-width forms -> ASCII), other non-ASCII characters
 * replaced by `DROPPED`, control characters removed, Windows-forbidden characters (and folded separators) `_`.
 */
function fold(text: string): string {
  let out = '';
  for (const ch of text.normalize('NFKD')) {
    const cp = ch.codePointAt(0) ?? 0;
    if (isCombiningMark(cp)) continue;
    if (cp >= 0x80) out += DROPPED;
    else if (cp < 0x20 || cp === 0x7f) continue;
    else out += WINDOWS_FORBIDDEN.has(ch) ? '_' : ch;
  }
  return out;
}

/** Windows device names, also with an extension or trailing spaces (`CON.txt`, `nul .png`). */
function isWindowsDevice(name: string): boolean {
  const dot = name.indexOf('.');
  return WINDOWS_DEVICE_RE.test((dot < 0 ? name : name.slice(0, dot)).trimEnd());
}

/**
 * One path segment made safe (see the module header). The result is a fixed point: a safe segment comes back
 * unchanged, so assets named on import keep their names on export.
 */
function cleanSegment(seg: string, isLast: boolean): string {
  // Trailing dots and spaces vanish on Windows; folding can produce them ("a.png" + U+00A8 folds to "a.png ").
  const folded = fold(seg).replace(TRAILING_DOTS_SPACES_RE, '');
  const lost = folded.includes(DROPPED);
  let { stem, ext } = isLast ? splitExt(folded) : { stem: folded, ext: '' };
  if (ext.length > MAX_EXT) {
    stem += ext;
    ext = '';
  }
  stem = stem.split(DROPPED).join('');
  ext = ext.split(DROPPED).join('').replace(TRAILING_DOTS_SPACES_RE, '');
  if (ext === '') stem = stem.replace(TRAILING_DOTS_SPACES_RE, '');
  if (lost) {
    // Separators left where dropped characters were ("bg 图" -> "bg") would only stand before the checksum.
    stem = stem.replace(/[\s_-]+$/, '');
    stem = `${stem === '' ? 'file' : stem}-${checksum(seg)}`;
  }
  if (isLast && ext === '') {
    // Split what is left once more, as a second pass would: the extension that was found may have vanished with the
    // dropped characters, leaving an earlier dot that now starts one ("..a." + U+66F2 -> "..a-<crc>", which splits
    // into "." and ".a-<crc>"). Without this the result would not be a fixed point.
    const again = splitExt(stem);
    if (again.ext.length <= MAX_EXT) ({ stem, ext } = again);
  }
  if (stem === '' || stem === '.' || stem === '..') stem = 'file';
  if (isWindowsDevice(stem)) stem = `_${stem}`;
  if (stem.length + ext.length > MAX_SEGMENT) {
    stem = `${stem.slice(0, MAX_SEGMENT - ext.length - 9)}-${checksum(seg)}`;
  }
  return stem + ext;
}

function isReservedPath(path: string, role?: AssetRole): boolean {
  if (RESERVED_ROOT_NAMES.has(path)) return true;
  if (path.endsWith('click.png')) return true;
  return role === 'hitsound' && BUILTIN_HITSOUND_NAMES.has(path);
}

/** A reserved path made usable (see the module header). */
function unreserve(path: string, role?: AssetRole): string {
  if (role === 'hitsound' && BUILTIN_HITSOUND_NAMES.has(path)) return `sfx/${path}`;
  const slash = path.lastIndexOf('/');
  const dir = path.slice(0, slash + 1);
  const { stem, ext } = splitExt(path.slice(slash + 1));
  return `${dir}${stem}_${ext}`;
}

function withSuffix(path: string, n: number): string {
  const slash = path.lastIndexOf('/');
  const dir = path.slice(0, slash + 1);
  const { stem, ext } = splitExt(path.slice(slash + 1));
  return `${dir}${stem}-${String(n)}${ext}`;
}

function sanitize(desired: string, taken: ReadonlySet<string>, role?: AssetRole): string {
  const raw = desired.split(/[\\/]/).filter((s) => s !== '' && s !== '.' && s !== '..');
  const segs = raw.map((s, i) => cleanSegment(s, i === raw.length - 1));
  let path = segs.length === 0 ? 'file' : segs.join('/');
  for (let guard = 0; guard < 4 && isReservedPath(path, role); guard++) path = unreserve(path, role);
  const lower = new Set<string>();
  for (const t of taken) lower.add(t.toLowerCase());
  if (!lower.has(path.toLowerCase())) return path;
  for (let n = 2; ; n++) {
    const candidate = withSuffix(path, n);
    if (!lower.has(candidate.toLowerCase()) && !isReservedPath(candidate, role)) return candidate;
  }
}

const FILE_NAME_FORBIDDEN = new Set(['\\', '/', ':', '*', '?', '"', '<', '>', '|']);

function cleanFileNamePart(s: string): string {
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp < 0x20 || cp === 0x7f || (cp >= 0x80 && cp <= 0x9f)) continue;
    out += FILE_NAME_FORBIDDEN.has(ch) ? '_' : ch;
  }
  return out.replace(/\s+/g, ' ').trim();
}

/** Language-neutral name of a download without a title or fallback (the editor's name). */
export const NEUTRAL_FILE_NAME = 'phiedit';

const MAX_FILE_NAME_CHARS = 150;

/** At most `MAX_FILE_NAME_CHARS` characters (code points), without trailing dots and spaces (Windows drops them). */
function fileNameBase(s: string): string {
  let count = 0;
  let cut = '';
  for (const ch of s) {
    if (count++ >= MAX_FILE_NAME_CHARS) break;
    cut += ch;
  }
  return cut.replace(/[.\s]+$/, '');
}

function packageFileName(title: string, level: string, ext: 'pez' | 'zip' | 'json', fallback?: string): string {
  const t = cleanFileNamePart(title);
  const l = cleanFileNamePart(level);
  const f = fileNameBase(cleanFileNamePart(fallback ?? ''));
  const untitled = f === '' ? NEUTRAL_FILE_NAME : f;
  let base = fileNameBase(`${t === '' ? untitled : t}${l === '' ? '' : ` [${l}]`}`);
  if (base === '') base = untitled;
  if (isWindowsDevice(base)) base = `_${base}`;
  return `${base}.${ext}`;
}

export const Names: AssetNamesApi = {
  sanitize,
  isReserved: isReservedPath,
  packageFileName,
};
